import { TRawPagination } from "../types";
import { TAccessRight } from "../businessUnit/types";

export type TRawPayload<T> = {
  idBu?: string;
  data: T;
};

export type TRawResponse<T> = {
  message: string;
  statusCode: string;
  refId: string;
  data: T;
};

export type TCreateTagReq = TRawPayload<{
  title: string;
  entity: string;
}>;

export type TCreateTagRes = {
  message: string;
  data: {
    id: string;
    title: string;
    slug: string;
  };
};

export type TGetTagsReq = TRawPayload<{
  keyword?: string;
  entity?: string;
}>;

export type TTag = {
  id: string;
  title: string;
  slug: string;
};

export type TGetTagsRes = TRawResponse<TTag[]>;

export type TDeleteTagReq = TRawPayload<{
  id: string;
}>;

export type TDeleteTagRes = TRawResponse<any>;

export type TUpdateTagReq = TRawPayload<{
  id: string;
  title: string;
}>;

export type TUpdateTagRes = TRawResponse<any>;

export enum EPipelineSlug {
  "INSIDE_SALES" = "inside-sales",
  "FIELD_SALES" = "field-sales",
  "CUSTOMER_SUCCESS" = "customer-success",
  "AD_HOC_TASKS" = "ad-hoc-tasks",
}

export enum EPriority {
  "MOST_IMPORTANT" = "MOST_IMPORTANT",
  "NORMAL" = "NORMAL",
  "IMPORTANT" = "IMPORTANT",
}

export type TPipelineStatus = {
  id: string;
  name: string;
  slug: string;
  index: number;
  createdAt?: string;
};

export enum ENotesEntity {
  BUSINESS_UNIT = "business-unit",
  STAFF = "staff",
  CORPORATION = "corporation",
}

export type TGetNotesReq = {
  idBu: string;
  data: {
    page?: number;
    pageSize?: number;
    filter: {
      keyword?: string;
      startDate?: string;
      endDate?: string;
      idPipelineStatus?: string[];
      idTicket?: string[];
      staffType?: string[];
      interviewType?: string[];
    };
    pipelineSlug?: EPipelineSlug;
    sortKey?: string;
    sortType?: string;
  };
  urlData: {
    entityType: ENotesEntity;
    entityId: string;
  };
};

export type TNotesTicket = {
  id: string;
  code: string;
  title: string;
  slug: EPipelineSlug;
  clientEngagementData?: TClientEngagementData;
  staffAndClientMatchingData?: {
    id: string;
    joblisting?: TJobListing;
  };
  businessUnit?: {
    idUnique: string;
  };
  priority: EPriority;
  dueDate: string;
  isArchived: boolean;
  idOperator: string;
  idPipeline: string;
  idLatestStatus: string;
  statusIndex: number;
  idBusinessUnit: string;
  createdAt: string;
};

export type TNotesProfile = {
  idUnique: string;
  fullName: string;
  preferredName: string;
  profilePicture: string;
  type: string;
};

export type TGetNotesRes = TRawPagination<{
  id: string;
  note: string;
  recentConversationNotes: string;
  notes: string;
  tips: string;
  pipelineStatus: TPipelineStatus;
  ticket?: TNotesTicket;
  submitter: TNotesProfile;
  staff?: TNotesProfile;
  createdAt: string;
  updatedAt?: string;
  staffEngagementData?: TStaffEngagementData;
  clientEngagementData?: TClientEngagementData;
  staffAndClientMatchingData?: TStaffAndClientMatchingData;
}>;

export type TGetPipelineStatusListRes = {
  data: TPipelineStatus[];
};

export type TGetPipelineStatusListReq = {
  pipelineSlug?: string;
  idBu?: string;
};

export enum EOverdueStatus {
  OVERDUE = "OVERDUE",
  NOT_OVERDUE = "NOT_OVERDUE",
}

type TCorporation = {
  id: string;
  idUnique: string;
  name: string;
  profilePicture: string;
};

type TOrganizationBusinessEstablishment = {
  id: string;
  corporation: TCorporation;
};

type TJobListingOrganization = {
  id: string;
  name: string;
  address: {
    id: string;
    name: string;
    city: string;
    perfecture: string;
    country: string;
  };
  businessEstablishment?: TOrganizationBusinessEstablishment;
};

type TJobListing = {
  id: string;
  title: string;
  organization?: TJobListingOrganization;
};

type TKanbanUser = {
  idUnique: string;
  fullName: string;
  preferredName: string;
  profilePicture?: string;
  gender: string;
  type: string;
  dob: string;
};

type TStaffEngagementData = {
  id: string;
  documentChecklist: string[];
  staff: TKanbanUser;
  employee: TKanbanUser;
  rejectedCorporations?: { corporation: TCorporation }[];
};

type TClientEngagementData = {
  id: string;
  client: TKanbanUser;
  employee: TKanbanUser;
  corporation?: TCorporation;
  rejectedStaff?: { staff: TKanbanUser }[];
};

type TStaffAndClientMatchingData = {
  id: string;
  client: TKanbanUser;
  staff: TKanbanUser;
  joblisting?: TJobListing;
  employee: TKanbanUser;
};

export type TGetTicketListRes = TRawResponse<
  {
    id: string;
    code: string;
    title: string;
    descriptions: string;
    slug: string;
    priority: EPriority;
    dueDate: string;
    overdueStatus?: EOverdueStatus;
    createdAt: string;
    idPipeline: string;
    idLatestStatus: string;
    averageProgress?: number;
    joblisting?: TJobListing;
    staff?: TKanbanUser;
    customerSuccess?: TKanbanUser;
    staffEngagementData?: TStaffEngagementData;
    clientEngagementData?: TClientEngagementData;
    staffAndClientMatchingData?: TStaffAndClientMatchingData;
  }[]
>;

export type TGetTicketListGlobalReq = {
  data: {
    page: number;
    pageSize: number;
    filter: {
      keyword?: string;
      idPipeline?: string;
    };
  };
};

export type TGetTicketListGlobalRes = TGetTicketListRes;

export type TGetTicketListReq = {
  idBu: string;
  data: {
    page: number;
    pageSize: number;
    filter: {
      keyword?: string;
      idOperator?: string;
      idPipeline?: string;
    };
    sortKey?: string;
    sortType?: string;
  };
};

export type TGetStaffTagsAllReq = {
  page?: number;
  pageSize?: number;
};

export type TStaffAllTagsList = {
  id: string;
  title: string;
};

export type TGetStaffTagsAllRes = {
  page: number;
  pageSize: number;
  total: number;
  data: TStaffAllTagsList[];
};

export type TPipeline = {
  id: string;
  name: string;
  managedBy: string;
  slug: EPipelineSlug;
  key: string;
};

export type TGetUsersPipelineRes = {
  data: TPipeline[];
};

export type TGetUsersPipelineReq = {
  buId?: string;
};

export type TGetBUScorecardHistoryStaffReq = TRawPayload<{
  page: number;
  pageSize: number;
  filter: {
    keyword?: string;
    idStaff?: string[];
    startDate?: string;
    endDate?: string;
    idPipelineStatus?: string[];
    idTicket?: string[];
    idEmployee?: string[];
  };
  sortKey?: string;
  sortType?: string;
}>;

export type TTicket = {
  id: string;
  code: string;
  title: string;
  slug: string;
  priority: string;
  isArchived: boolean;
  idOperator: string;
  idPipeline: string;
  idLatestStatus: string;
  statusIndex: number;
  idBusinessUnit: string;
  createdAt: string;
  pipeline: TPipeline;
  pipelineStatus?: TPipelineStatus;
  staffEngagementData: TStaffEngagementData;
  clientEngagementData: TClientEngagementData;
};

export type TScorecardContentStaff = {
  id: string;
  idScorecard: string;
  leadNeeds: string;
  leadTiming: number;
  leadLikelihood: number;
  leadOnlineBehavior: number;
  channel: string[];
  createdAt: string;
};

export type TScorecardHistoryStaff = {
  id: string;
  idTicket: string;
  idPipelineStatus: string;
  pipelineStatus: TPipelineStatus;
  ticket: TTicket;
  contentStaff: TScorecardContentStaff;
  createdAt: string;
};

export type TGetBUScorecardHistoryStaffRes =
  TRawPagination<TScorecardHistoryStaff>;

export type TGetKanbanTicketsReq = {
  buId?: string;
  data: {
    filter: {
      keyword?: string;
      priority?: string[];
      operators?: string[];
      status?: string[];
      tags?: string[];
      dueStatus?: string;
      showAllMatching?: boolean;
    };
  };
  path: {
    pipelineSlug?: EPipelineSlug;
  };
};

type TAdHocTaskData = {
  id: string;
  employee?: TKanbanUser;
  relatedTicket?: {
    id: string;
    operator: TKanbanUser;
  };
};

type TKanbanTag = {
  idBusinessUnit: string;
  id: string;
  title: string;
  slug: string;
  idEntity: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  entity: TEntityTag;
};

type TEntityTag = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
};

export type TAttachment = {
  id: string;
  name: string;
  url: string;
};

export type TNotes = {
  id: string;
  recentConversationNotes: string;
  notes: string;
  tips: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
};

export type TKanbanTicket = {
  id: string;
  code: string;
  title: string;
  descriptions: string;
  slug: string;
  priority: EPriority;
  dueDate?: string;
  isArchived: boolean;
  pipelineId: string;
  idLatestStatus: string;
  createdAt: string;
  operator: TKanbanUser;
  overdueStatus?: EOverdueStatus;
  staffEngagementData?: TStaffEngagementData;
  clientEngagementData?: TClientEngagementData;
  staffAndClientMatchingData?: TStaffAndClientMatchingData;
  adHocTasksData?: TAdHocTaskData;
  tags?: TKanbanTag[];
  attachments?: TAttachment[];
  notes?: TNotes[];
  statusIndex: number;
  idBussinessUnit: string;
  pipelineStatus?: {
    id: string;
    name: string;
    slug: string;
  };
};

export type TKanbanStatus = {
  id: string;
  name: string;
  slug: string;
  index: number;
  tickets: TKanbanTicket[];
};

export type TGetKanbanTicketsRes = {
  data: TKanbanStatus[];
};

export type TGetBUScorecardHistoryCorporationReq = TRawPayload<{
  page: number;
  pageSize: number;
  filter: {
    keyword?: string;
    idCorporation?: string[];
    startDate?: string;
    endDate?: string;
    idPipelineStatus?: string[];
    idTicket?: string[];
    idEmployee?: string[];
  };
  sortKey?: string;
  sortType?: string;
}>;

export type Tcounterpart = {
  idUnique: string;
  fullName: string;
  preferredName: string;
  profilePicture: string;
  type: string;
};

export type TScorecardContentClient = TScorecardContentStaff & {
  leadRevenue: number;
  leadSize: number;
  counterpartAuthority: string;
  counterpart: Tcounterpart;
};

export type TScorecardHistoryCorporation = Omit<
  TScorecardHistoryStaff,
  "contentStaff"
> & {
  contentClient: TScorecardContentClient;
};

export type TGetBUScorecardHistoryCorporationRes =
  TRawPagination<TScorecardHistoryCorporation>;

export type TUpdateScorecardStaffReq = {
  idBu: string;
  data: TScorecardMainReq;
  urlData: {
    idScorecard: string;
  };
};

export type TUpdateScorecardStaffRes = TRawResponse<any>;

export type TScorecardMainReq = {
  leadNeeds: string;
  leadTiming: number;
  leadLikelihood: number;
  leadOnlineBehavior: number;
  channels: string[];
};

export type TUpdateScorecardClientReq = {
  idBu: string;
  data: TScorecardMainReq & {
    counterpartAuthority: string;
    leadRevenue: number;
    leadSize: number;
  };
  urlData: {
    idScorecard: string;
  };
};

export type TUpdateScorecardClientRes = TRawResponse<any>;

export type TGetScorecardDetailsReq = {
  idBu: string;
  urlData: {
    idScorecard: string;
  };
};

export type TScorecardDetailContentStaff = {
  id: string;
  idScorecard: string;
  leadNeeds: string;
  leadTiming: string;
  leadLikelihood: string;
  leadOnlineBehavior: string;
  channel: string[];
  createdAt: string;
};

export type TScorecardDetailContentClient = TScorecardDetailContentStaff & {
  leadRevenue: string;
  leadSize: string;
  counterpartAuthority: string;
};

export type TModifier = {
  fullName: string;
  idUnique: string;
  preferredName: string;
  profilePicture: string;
  type: string;
};

export type TScorecardDetail = {
  id: string;
  idTicket: string;
  idPipelineStatus: string;
  contentClient?: TScorecardDetailContentClient;
  contentStaff?: TScorecardDetailContentStaff;
  createdAt: string;
  modifier: TModifier;
};

export type TGetScorecardDetailsRes = TRawResponse<{
  latest: TScorecardDetail;
  history: TScorecardDetail[];
}>;

export type TGetTicketDetailsReq = {
  pipelineSlug: string;
  idTicket: string;
  idBu: string;
};

export type TGetTicketDetailsRes = {
  data: TKanbanTicket;
};

export type TGetUserAppAccessRightReq = TRawPayload<{
  idUser: string;
}>;

export type TUserAccessRight = {
  position: string;
  access: TAccessRight[];
};

export type TGetUserAppAccessRightRes = TRawResponse<TUserAccessRight>;
